import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignVideoSource } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface EmbedVideoFormValues {
    platform: CampaignVideoSource;
    url: string;
}

const CampaignCoverEmbedVideoForm = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const isVideo = campaign?.cover?.type === 'video';
    const methods = Form.useForm<EmbedVideoFormValues>({
        defaultValues: {
            platform: isVideo ? (campaign?.cover?.source ?? 'youtube') : 'youtube',
            url: isVideo ? (campaign?.cover?.url ?? '') : '',
        },
    });
    const { handleSubmit, control, path } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: EmbedVideoFormValues) => {
            const data = {
                cover: {
                    source: values.platform,
                    type: 'video',
                    url: values.url,
                },
            };

            return updateCampaign({ campaignId: campaignId, data });
        },
        onSuccess: () => {
            toast.success('Updated cover photo settings!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update cover photo settings'),
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<EmbedVideoFormValues>)}>
                <div className={'flex flex-col gap-4'}>
                    <Form.Container>
                        <Form.Label className={'text-base'} name={path('platform')}>
                            Video platform
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.SingleSelect
                            control={control}
                            name="platform"
                            rules={{ required: 'Please select a video platform' }}
                        >
                            <SingleSelect.Trigger aria-label="Video platform" />
                            <SingleSelect.Content>
                                <SingleSelect.Item value="youtube">
                                    <SingleSelect.ItemText>Youtube</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="vimeo">
                                    <SingleSelect.ItemText>Vimeo</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="twitch">
                                    <SingleSelect.ItemText>Twitch</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="facebook">
                                    <SingleSelect.ItemText>Facebook</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        <Form.Error name={path('platform')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label className={'text-base'} name={path('url')}>
                            Video URL
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={path('url')}
                            rules={{ required: 'Please enter a url' }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('url')} />
                    </Form.Container>
                    <div>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

export { CampaignCoverEmbedVideoForm };
